import React, { Fragment, Suspense, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { Container, Divider, Row } from "../components";
import { PulseLoader } from "../components/pulse_loader";
import { useGetPartnerOffering } from "../data/api";
import { CatalogOffering, PartnerCatalog } from "../data/catalog";
import { buildPathWithParams, useMediaQuery, useQueryString } from "../hooks";
import { useAnalytics } from "../lib/analytics/analytics";
import { useI18n } from "../lib/i18n/i18n_provider";
import { Button } from "./button";
import { CatalogFooterMobile, CatalogHeader } from "./catalog_header";
import { Footer } from "./footer";
import { Grid } from "./grid";
import { Heading } from "./heading";
import { PartnerConfig } from "./partner_config";
import { Spacer } from "./spacer";
import { UpsellCard } from "./template_catalog_base";
import { Text } from "./text";
import { CatalogPartnerInfo } from "./catalog_partner_info";
import { FilteredCatalog } from "@/offering_v2/components/filters/filtered_catalog";
import { FilterLocation } from "@/data/partner_offering";
import { useDisplayMerchantNameAndAddress } from "@/core/splitio";
import { useUTMParams } from "./utm";

interface TemplateCatalogOriginalProps {
  partnerConfig: PartnerConfig;
  partnerCatalog: PartnerCatalog;
  locationAdress?: string;
  catalogLocationFilter: React.ReactNode;
  locations: FilterLocation[];
}

export const TemplateCatalogOriginal = (
  props: TemplateCatalogOriginalProps
) => {
  /**
   * Aka old "V1" catalog:
   * - One catalog has many group (`partnerCatalogCategories`)
   * - Each group has many item (`CatalogOfferings`)
   */
  const {
    partnerCatalog,
    partnerConfig,
    locationAdress,
    catalogLocationFilter,
    locations,
  } = props;
  const { hubspotForm, favicon, localeOptions, showLanguageSelector } =
    partnerConfig;
  const {
    id,
    title,
    description,
    partner,
    partnerCatalogUpsell,
    partnerCatalogCategories: categories,
  } = partnerCatalog;
  const {
    id: partnerId,
    name: partnerName,
    homepageURL,
    logo: partnerLogo,
  } = partner;
  const mq = useMediaQuery();
  const { locationID } = useQueryString();
  const { utmCampaign } = useUTMParams();
  const displayMerchantNameAndAddress = useDisplayMerchantNameAndAddress();

  return (
    <Container paddingHorizontal={12}>
      <Helmet>
        <title>{partnerName} Catalog Page</title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      <div className="content">
        <CatalogHeader
          messageableID={id}
          homepageURL={homepageURL}
          logo={partnerLogo}
          showLanguageSelector={showLanguageSelector}
          hubspotForm={hubspotForm}
          partnerID={partnerId}
          localeOptions={localeOptions}
        />
      </div>
      <div className="content">
        <div style={{ paddingTop: 44, paddingBottom: 54 }}>
          {displayMerchantNameAndAddress &&
            utmCampaign === "g-book-now" &&
            partnerName &&
            locationAdress && (
              <CatalogPartnerInfo
                partnerName={partnerName}
                locationAdress={locationAdress}
              />
            )}
          <Heading size="xl">{title}</Heading>
          <Spacer size={24} />
          <Text size="md">
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </Text>
          {catalogLocationFilter}
        </div>
        <Container>
          {locationID && !!catalogLocationFilter ? (
            <FilteredCatalog
              catalog={partnerCatalog}
              catalogSlug={id}
              locations={locations}
            />
          ) : (
            categories.map((catalogCategory, index) => (
              <Fragment>
                <div className="catalog-category">
                  {mq.device === "desktop" ? (
                    <Row justifyContent="center">
                      <Container width={304} paddingRight={16}>
                        <Heading size="lg">{catalogCategory.title}</Heading>
                      </Container>
                      <Container width={624}>
                        <OriginalTemplateGroup
                          partnerID={partnerCatalog.partner.id}
                          offerings={catalogCategory.catalogOfferings}
                          partnerSlug={partnerCatalog.partner.partnerSlug}
                          catalogSlug={partnerCatalog.slug}
                        />
                      </Container>
                    </Row>
                  ) : (
                    <Fragment>
                      <Heading size="lg">{catalogCategory.title}</Heading>
                      <Spacer size={24} />
                      <OriginalTemplateGroup
                        partnerID={partnerCatalog.partner.id}
                        offerings={catalogCategory.catalogOfferings}
                        partnerSlug={partnerCatalog.partner.partnerSlug}
                        catalogSlug={partnerCatalog.slug}
                      />
                    </Fragment>
                  )}
                </div>
                {index !== categories.length - 1 && <Divider />}
              </Fragment>
            ))
          )}
        </Container>
        <Spacer size={30} />
        {partnerCatalogUpsell && (
          <UpsellCard
            image={partnerCatalogUpsell.image}
            title={partnerCatalogUpsell.title}
            description={partnerCatalogUpsell.description}
            learnMoreURL={partnerCatalogUpsell.learnMoreURL}
            ctaLabel={partnerCatalogUpsell.ctaLabel}
            longDescription={partnerCatalogUpsell.longDescription}
          />
        )}
      </div>
      <Spacer size={24} />
      <CatalogFooterMobile
        hubspotForm={hubspotForm}
        showLanguageSelector={showLanguageSelector}
        messageableID={id}
        partnerID={partnerId}
        localeOptions={localeOptions}
      />
      <Footer />
    </Container>
  );
};

interface OriginalTemplateGroupProps {
  partnerSlug: string;
  catalogSlug?: string;
  partnerID: string;
  offerings?: CatalogOffering[];
}

export const OriginalTemplateGroup = (props: OriginalTemplateGroupProps) => {
  /**
   * For "original" template (aka "V1" catalog)
   * Render one category and its offering items
   */
  const { offerings, partnerSlug, catalogSlug, partnerID } = props;
  const history = useHistory();
  const { fbt } = useI18n();

  return (
    <Container>
      <Grid itemsPerRow={{ md: 2, default: 1 }} spacing={16}>
        {offerings?.map((o) => {
          const { image, title, description, slug, multiPassOffering } = o;

          return (
            <Fragment>
              <Container key={title} paddingBottom={16}>
                <Suspense
                  fallback={
                    <Container minHeight={240} center>
                      <PulseLoader />
                    </Container>
                  }
                >
                  <CatalogOfferingCard
                    partnerID={partnerID}
                    image={image}
                    title={title}
                    description={description}
                    offeringSlug={slug}
                    catalogSlug={catalogSlug}
                  />
                </Suspense>
              </Container>
              {multiPassOffering && (
                <Container center paddingBottom={8}>
                  <Text
                    color="link"
                    onPress={() => {
                      const pathWithParams = buildPathWithParams(
                        `/portal/${partnerSlug}`
                      );
                      history.push(pathWithParams);
                    }}
                  >
                    {fbt("Manage bookings")}
                  </Text>
                </Container>
              )}
            </Fragment>
          );
        })}
      </Grid>
    </Container>
  );
};

interface CatalogOfferingCardProps {
  image: string;
  title: string;
  description: string;
  offeringSlug: string;
  catalogSlug?: string;
  partnerID: string;
  comingSoon?: boolean;
}

// Only use for "original" template
function CatalogOfferingCard(props: CatalogOfferingCardProps) {
  const { image, title, description, offeringSlug, catalogSlug, comingSoon } =
    props;
  const history = useHistory();
  const { fbt } = useI18n();
  const analytics = useAnalytics();
  const partnerOffering = useGetPartnerOffering(offeringSlug);

  const handleOpen = useCallback(() => {
    analytics.event(
      `Catalog Offering Card Click`,
      {
        Template: partnerOffering.template,
        Title: title,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
    const pathWithParams = buildPathWithParams(
      catalogSlug
        ? `/offerings/${offeringSlug}?fromCatalog=${catalogSlug}`
        : `/offerings/${offeringSlug}`,
      { reset: true, keepOnReset: ["fromCatalog", "promoCode", "email"] }
    );
    history.push(pathWithParams);
  }, [
    analytics,
    catalogSlug,
    history,
    offeringSlug,
    partnerOffering.template,
    title,
  ]);

  return (
    <div className="catalog-offering-card">
      <img
        alt={title}
        style={{
          objectFit: "cover",
          width: "100%",
          height: 173,
          borderTopLeftRadius: 5,
          borderTopRightRadius: 5,
        }}
        src={image}
      />
      <Spacer size={8} />
      <Container flex={1} paddingRight={11} paddingLeft={11} paddingBottom={19}>
        {/* minHeight able to fit 1 line of card title & 2 lines of card description, maintaining equal heights of cards */}
        <Container minHeight={76}>
          <div className="catalog-offering-card-title">
            <Heading size="sm">{title}</Heading>
          </div>
          <Spacer size={4} />
          <div className="catalog-offering-card-description">
            <Text>{description}</Text>
          </div>
        </Container>
        <Spacer size={24} />
        <Spacer />
        <Button
          title={comingSoon ? fbt("Coming Soon") : fbt("Book now")}
          disabled={comingSoon}
          onPress={handleOpen}
        />
      </Container>
    </div>
  );
}
